import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { TimezoneService } from '../services/timezoneService';
import { exportScheduledEmployeesToCSV, exportScheduledEmployeesToExcel, exportScheduledEmployeesToPDF } from '../utils/exportUtils';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFilterService } from '../services/campaignFilterService';
import { Campaign, User } from '../types';
import { getApprovedAbsenceDisplayLabel } from '../constants/absenceReasons';

interface ScheduledEmployee {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  department: string;
  role: string;
  scheduleId: string;
  scheduleDate: string;
  startTime: string;
  endTime: string;
  startTimeUTC: string;
  endTimeUTC: string;
  campaignName: string;
  hasClockedIn: boolean;
  clockInTime?: string;
  clockInTimeFormatted?: string | null;
  clockOutTime?: string;
  clockOutTimeFormatted?: string | null;
  currentStatus?: string;
  timezone: string;
}

interface ScheduledEmployeesResponse {
  success: boolean;
  data: ScheduledEmployee[];
  total: number;
  date: string;
  timezone: string;
}

const ScheduledEmployeesReportPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    // Get today's date in local timezone (not UTC)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [employeeFilter, setEmployeeFilter] = useState<string>('');
  const [incidentFilter, setIncidentFilter] = useState<string>('');
  const [incidentDescriptionFilter, setIncidentDescriptionFilter] = useState<string[]>([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'department' | 'start' | 'campaign' | 'status'>('start');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // newest first by default

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch campaigns for filter dropdown (active + inactive with activity on selected date)
  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', selectedDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: selectedDate,
      includeWithActivityTo: selectedDate,
    }),
    enabled: !!user && ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch employees for filter dropdown
  const { data: employeesData } = useQuery<{ data: { success: boolean; data: User[] } }, Error>({
    queryKey: ['users'],
    queryFn: apiClient.users.getAll,
    enabled: !!user && ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Filter campaigns based on user's assigned campaigns
  const availableCampaigns = campaignsData?.data?.data ? 
    CampaignFilterService.getUserAccessibleCampaigns(user!, campaignsData.data.data) : [];

  const {
    data: scheduledEmployees,
    isLoading,
    error,
    refetch,
  } = useQuery<ScheduledEmployeesResponse>({
    queryKey: ['scheduled-employees', selectedDate, departmentFilter, statusFilter, campaignFilter, employeeFilter],
    queryFn: async () => {
      const response = await apiClient.reports.getScheduledEmployees({
        date: selectedDate,
        department: departmentFilter !== 'all' ? departmentFilter : undefined,
        status: statusFilter ? statusFilter : undefined,
        campaign: campaignFilter || undefined,
        employee: employeeFilter || undefined,
      });
      return response.data;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch approved absences for the selected date to mark "Absent (Approved)"
  const { data: absencesData } = useQuery({
    queryKey: ['all-absences', selectedDate],
    queryFn: async () => {
      try {
        const allAbsences: any[] = [];
        let page = 1;
        let total = 0;
        const limit = 500;

        while (true) {
          const resp = await apiClient.absences.getAll(`page=${page}&limit=${limit}`);
          const pageData = resp.data?.data || [];
          const pagination = resp.data?.pagination;
          allAbsences.push(...pageData);
          total = pagination?.total ?? allAbsences.length;

          if (allAbsences.length >= total || pageData.length === 0) {
            break;
          }
          page += 1;
        }

        return allAbsences;
      } catch (e) {
        return [] as any[];
      }
    },
    staleTime: 60000,
  });

  const approvedAbsentUserIds = React.useMemo(() => {
    if (!absencesData) return new Set<string>();
    const set = new Set<string>();
    absencesData.forEach((a: any) => {
      const sd = String(a.startDate);
      const ed = String(a.endDate);
      if (sd <= selectedDate && ed >= selectedDate && a.status === 'approved') {
        if (a.userId) set.add(a.userId);
      }
    });
    return set;
  }, [absencesData, selectedDate]);

  const absencesByUserId = React.useMemo(() => {
    const map = new Map<string, any[]>();
    if (!absencesData) return map;
    absencesData.forEach((a: any) => {
      const sd = String(a.startDate);
      const ed = String(a.endDate);
      if (sd <= selectedDate && ed >= selectedDate && a.userId) {
        const list = map.get(a.userId) || [];
        list.push(a);
        map.set(a.userId, list);
      }
    });
    return map;
  }, [absencesData, selectedDate]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleDepartmentChange = (event: any) => {
    setDepartmentFilter(event.target.value);
  };

  const handleStatusChange = (event: any) => {
    setStatusFilter(event.target.value);
  };

  const handleCampaignChange = (event: any) => {
    setCampaignFilter(event.target.value);
  };

  const handleEmployeeChange = (event: any) => {
    setEmployeeFilter(event.target.value);
  };

  const handleIncidentChange = (event: any) => {
    setIncidentFilter(event.target.value);
  };

  const getIncidentInfo = (employee: ScheduledEmployee) => {
    const incidents: string[] = [];
    const absenceRecords = absencesByUserId.get(employee.id) || [];
    if (absenceRecords.length > 0) {
      const absenceLabels = absenceRecords.map((a: any) => getApprovedAbsenceDisplayLabel({
        absenceType: a.absenceType,
        reason: a.reason,
      }));
      const uniqueAbsenceLabels = Array.from(new Set(absenceLabels));
      incidents.push(`Absence: ${uniqueAbsenceLabels.join(', ')}`);
      return {
        hasIncident: true,
        hasAbsence: true,
        incidentTypes: incidents
      };
    }

    const scheduleStart = new Date(employee.startTimeUTC);
    const scheduleEnd = new Date(employee.endTimeUTC);

    const getLateSeverityLabel = (minutesLate: number) => {
      if (minutesLate > 120) return 'Severely Late';
      if (minutesLate > 30) return 'Late';
      return 'Slightly Late';
    };

    if (employee.clockInTime) {
      const clockIn = new Date(employee.clockInTime);
      if (clockIn > scheduleStart) {
        incidents.push('Late Start');
      }
    } else {
      const now = new Date();
      if (now >= scheduleStart) {
        const minutesLate = Math.floor((now.getTime() - scheduleStart.getTime()) / (1000 * 60));
        incidents.push(`${getLateSeverityLabel(minutesLate)} (No Clock-In)`);
      }
      if (now > scheduleEnd) {
        incidents.push('Missed');
      }
    }

    if (employee.clockOutTime) {
      const clockOut = new Date(employee.clockOutTime);
      if (clockOut < scheduleEnd) {
        incidents.push('Early End');
      }
    }

    return {
      hasIncident: incidents.length > 0,
      hasAbsence: false,
      incidentTypes: incidents
    };
  };

  const incidentDescriptionOptions = React.useMemo(() => {
    const options = new Set<string>([
      'Late Start',
      'Early End',
      'Missed',
      'Severely Late (No Clock-In)',
      'Late (No Clock-In)',
      'Slightly Late (No Clock-In)',
      'Absence: vacation',
      'Absence: sick',
      'Absence: personal',
      'Absence: other',
      'Absence: unexcused',
      'Absence: holiday',
      'Absence: bereavement',
      'Absence: maternity',
      'Absence: jury_duty',
      'Absence: no_show',
      'Absence: training',
      'Absence: suspension'
    ]);

    (scheduledEmployees?.data || []).forEach((employee) => {
      const types = getIncidentInfo(employee).incidentTypes;
      types.forEach(type => options.add(type));
    });
    return Array.from(options).sort();
  }, [scheduledEmployees?.data]);

  const getRowBackground = (employee: ScheduledEmployee) => {
    const incidentInfo = getIncidentInfo(employee);
    if (!incidentInfo.hasIncident) {
      return 'inherit';
    }

    if (incidentInfo.hasAbsence) {
      return 'rgba(129, 212, 250, 0.18)'; // info
    }

    const now = new Date();
    const scheduleStart = new Date(employee.startTimeUTC);
    const scheduleEnd = new Date(employee.endTimeUTC);

    if (!employee.hasClockedIn) {
      if (now > scheduleEnd) {
        return 'rgba(239, 83, 80, 0.16)'; // error
      }
      if (now >= scheduleStart) {
        const minutesLate = Math.floor((now.getTime() - scheduleStart.getTime()) / (1000 * 60));
        if (minutesLate > 120) {
          return 'rgba(239, 83, 80, 0.16)'; // error
        }
        if (minutesLate > 30) {
          return 'rgba(255, 167, 38, 0.18)'; // warning
        }
        return 'rgba(129, 212, 250, 0.18)'; // info
      }
    }

    if (incidentInfo.incidentTypes.some((type) => type.includes('Early End') || type.includes('Late Start'))) {
      return 'rgba(255, 167, 38, 0.18)'; // warning
    }

    return 'inherit';
  };

  const getStatusLabelForExport = (employee: ScheduledEmployee) => {
    if (approvedAbsentUserIds.has(employee.id)) {
      return 'Absent (Approved)';
    }
    if (employee.hasClockedIn) {
      return employee.clockOutTime ? 'Completed' : 'Working';
    }
    const now = new Date();
    const scheduleStart = new Date(employee.startTimeUTC);
    const scheduleEnd = new Date(employee.endTimeUTC);
    if (now > scheduleEnd) {
      return 'Missed';
    }
    if (now >= scheduleStart) {
      const minutesLate = Math.floor((now.getTime() - scheduleStart.getTime()) / (1000 * 60));
      return minutesLate > 120 ? 'Severely Late' : minutesLate > 30 ? 'Late' : 'Slightly Late';
    }
    return 'Scheduled';
  };

  const getExportRows = () =>
    sortedEmployees.map((employee) => {
      const incidentInfo = getIncidentInfo(employee);
      const incidentDescription = incidentInfo.incidentTypes.length > 0
        ? incidentInfo.incidentTypes.join(', ')
        : 'None';
      return {
        ...employee,
        statusLabel: getStatusLabelForExport(employee),
        incidentDescription,
        clockInTimeExport: employee.clockInTimeFormatted
          || (employee.clockInTime ? new Date(employee.clockInTime).toLocaleString() : 'Not clocked in'),
        clockOutTimeExport: employee.clockOutTimeFormatted
          || (employee.clockOutTime ? new Date(employee.clockOutTime).toLocaleString() : 'Not clocked out')
      };
    });

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    if (sortedEmployees.length > 0) {
      exportScheduledEmployeesToCSV(getExportRows(), selectedDate);
    }
    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    if (sortedEmployees.length > 0) {
      exportScheduledEmployeesToExcel(getExportRows(), selectedDate);
    }
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    if (sortedEmployees.length > 0) {
      exportScheduledEmployeesToPDF(getExportRows(), selectedDate);
    }
    handleExportMenuClose();
  };

  const getStatusChip = (employee: ScheduledEmployee) => {
    // If approved absence overlaps today, show Absent (Approved)
    if (approvedAbsentUserIds.has(employee.id)) {
      return <Chip icon={<CheckCircleIcon />} label="Absent (Approved)" color="info" size="small" />;
    }
    if (employee.hasClockedIn) {
      if (employee.clockOutTime) {
        return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />;
      } else {
        return <Chip icon={<AccessTimeIcon />} label="Working" color="primary" size="small" />;
      }
    } else {
      const now = new Date();
      const scheduleStart = new Date(employee.startTimeUTC);
      const scheduleEnd = new Date(employee.endTimeUTC);
      
      if (now > scheduleEnd) {
        return <Chip icon={<WarningIcon />} label="Missed" color="error" size="small" />;
      } else if (now >= scheduleStart) {
        const minutesLate = Math.floor((now.getTime() - scheduleStart.getTime()) / (1000 * 60));
        const severityLabel = minutesLate > 120 ? 'Severely Late' : minutesLate > 30 ? 'Late' : 'Slightly Late';
        const severityColor = minutesLate > 120 ? 'error' : minutesLate > 30 ? 'warning' : 'info';
        return <Chip icon={<WarningIcon />} label={severityLabel} color={severityColor as any} size="small" />;
      } else {
        return <Chip icon={<ScheduleIcon />} label="Scheduled" color="info" size="small" />;
      }
    }
  };


  const getStatusCounts = () => {
    if (!scheduledEmployees?.data) return { total: 0, clockedIn: 0, notClockedIn: 0, late: 0, missed: 0 };
    
    const now = new Date();
    let clockedIn = 0;
    let notClockedIn = 0;
    let late = 0;
    let missed = 0;

    scheduledEmployees.data.forEach(employee => {
      if (employee.hasClockedIn) {
        clockedIn++;
      } else {
        notClockedIn++;
        const scheduleStart = new Date(employee.startTimeUTC);
        const scheduleEnd = new Date(employee.endTimeUTC);
        
        if (now > scheduleEnd) {
          missed++;
        } else if (now >= scheduleStart) {
          late++;
        }
      }
    });

    return {
      total: scheduledEmployees.data.length,
      clockedIn,
      notClockedIn,
      late,
      missed
    };
  };

  const statusCounts = getStatusCounts();

  const toggleSort = (col: 'employee' | 'department' | 'start' | 'campaign' | 'status') => {
    if (sortBy === col) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir(col === 'start' ? 'desc' : 'asc');
    }
  };

  const sortedEmployees = React.useMemo(() => {
    const list = scheduledEmployees?.data ? [...scheduledEmployees.data] : [];
    const incidentFilterValue = String(incidentFilter || '').toLowerCase().trim();
    const filtered = incidentFilterValue === ''
      ? list
      : list.filter((employee) => {
          const hasIncident = getIncidentInfo(employee).hasIncident;
          return incidentFilterValue === 'yes' ? hasIncident : !hasIncident;
        });

    const filteredByDescription = incidentDescriptionFilter.length === 0
      ? filtered
      : filtered.filter((employee) => {
          const types = getIncidentInfo(employee).incidentTypes;
          return incidentDescriptionFilter.some((selected) => types.includes(selected));
        });
    filteredByDescription.sort((a, b) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          av = `${a.lastName}${a.firstName}`.toLowerCase();
          bv = `${b.lastName}${b.firstName}`.toLowerCase();
          break;
        case 'department':
          av = (a.department || '').toLowerCase();
          bv = (b.department || '').toLowerCase();
          break;
        case 'campaign':
          av = (a.campaignName || '').toLowerCase();
          bv = (b.campaignName || '').toLowerCase();
          break;
        case 'status':
          const statusRank = (e: ScheduledEmployee) => approvedAbsentUserIds.has(e.id) ? 0 : e.hasClockedIn ? 1 : 2;
          av = statusRank(a); bv = statusRank(b);
          break;
        case 'start':
        default:
          av = new Date(a.startTimeUTC).getTime();
          bv = new Date(b.startTimeUTC).getTime();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return filteredByDescription;
  }, [scheduledEmployees?.data, sortBy, sortDir, approvedAbsentUserIds, incidentFilter, incidentDescriptionFilter, absencesByUserId]);

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load scheduled employees data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Scheduled Employees Report
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Track employees who are scheduled to work and their attendance status.
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Filter Row */}
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Department"
                  value={departmentFilter}
                  onChange={(value) => handleDepartmentChange({ target: { value } })}
                  options={[
                    { value: 'sales', label: 'Sales' },
                    { value: 'support', label: 'Support' },
                    { value: 'marketing', label: 'Marketing' },
                    { value: 'operations', label: 'Operations' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Campaign (Optional)"
                  value={campaignFilter}
                  onChange={(value) => handleCampaignChange({ target: { value } })}
                  options={availableCampaigns.map((campaign) => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Employee (Optional)"
                  value={employeeFilter}
                  onChange={(value) => handleEmployeeChange({ target: { value } })}
                  options={(employeesData?.data?.data || []).map((emp: User) => ({
                    value: emp.id,
                    label: `${emp.firstName} ${emp.lastName} (${emp.employeeId})`,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Status"
                  value={statusFilter}
                  onChange={(value) => handleStatusChange({ target: { value } })}
                  options={[
                    { value: 'scheduled', label: 'Scheduled' },
                    { value: 'late', label: 'Late' },
                    { value: 'missed', label: 'Missed' },
                    { value: 'working', label: 'Working' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Incidents"
                  value={incidentFilter}
                  onChange={(value) => handleIncidentChange({ target: { value } })}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={incidentDescriptionOptions}
                  value={incidentDescriptionFilter}
                  onChange={(_, newValue) => setIncidentDescriptionFilter(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Incident Description" placeholder="Select incidents" />
                  )}
                  disableCloseOnSelect
                  clearOnEscape
                />
              </Grid>
            </Grid>
            {/* Action Buttons Row */}
            <Grid container item xs={12} spacing={1} sx={{ mt: 1 }}>
              <Grid item>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={() => refetch()}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Search
                  </Button>
                  <Tooltip title="Refresh Data">
                    <IconButton onClick={() => refetch()}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!scheduledEmployees?.data || scheduledEmployees.data.length === 0}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => setShareDialogOpen(true)}
                      disabled={!scheduledEmployees?.data || scheduledEmployees.data.length === 0}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Share
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {statusCounts.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Scheduled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {statusCounts.clockedIn}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clocked In
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {statusCounts.late}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Late
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {statusCounts.missed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Missed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {statusCounts.notClockedIn}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Not Clocked In
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Scheduled Employees for {new Date(selectedDate + 'T00:00:00').toLocaleDateString()}
            </Typography>
            {isLoading && <CircularProgress size={24} />}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : scheduledEmployees?.data && scheduledEmployees.data.length > 0 ? (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('employee')}>Employee {sortBy==='employee'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('department')}>Department {sortBy==='department'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('start')}>Schedule {sortBy==='start'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('campaign')}>Campaign {sortBy==='campaign'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status {sortBy==='status'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                    <TableCell>Incident Description</TableCell>
                    <TableCell>Clock In Time</TableCell>
                    <TableCell>Clock Out Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEmployees.map((employee) => (
                    <TableRow key={employee.id} sx={{ backgroundColor: getRowBackground(employee) }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={employee.department} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {TimezoneService.convertScheduleToLocal(
                              employee.startTimeUTC,
                              employee.timezone
                            ).time} - {TimezoneService.convertScheduleToLocal(
                              employee.endTimeUTC,
                              employee.timezone
                            ).time}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.timezone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{employee.campaignName}</Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(employee)}
                      </TableCell>
                      <TableCell>
                        {getIncidentInfo(employee).incidentTypes.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {getIncidentInfo(employee).incidentTypes.map((type) => (
                              <Chip key={type} label={type} size="small" color="warning" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {employee.clockInTimeFormatted ? (
                          <Typography variant="body2">
                            {employee.clockInTimeFormatted}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not clocked in
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {employee.clockOutTimeFormatted ? (
                          <Typography variant="body2">
                            {employee.clockOutTimeFormatted}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not clocked out
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No scheduled employees found for the selected date and filters.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="scheduled-employees"
          reportFilters={{ date: selectedDate, department: departmentFilter, status: statusFilter }}
          reportTitle="Scheduled Employees Report"
          reportData={getExportRows()}
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default ScheduledEmployeesReportPage;
